import desertBgm from '../../../assets/cat-running/bgm/desert-bgm.mp3'
import forestBgm from '../../../assets/cat-running/bgm/forest-bgm.mp3'
import iceBgm from '../../../assets/cat-running/bgm/ice-bgm.mp3'
import volcanoBgm from '../../../assets/cat-running/bgm/volcano-gbm.mp3'
import { CAT_RUNNING_STAGE_KEYS } from '../constants'

const STAGE_BGM_SRC = {
  forest: forestBgm,
  desert: desertBgm,
  ice: iceBgm,
  volcano: volcanoBgm,
}

const TARGET_VOLUME = 0.35
const MUTE_STORAGE_KEY = 'cat-running-bgm-muted'

let muted = false
let activeStageKey = null
let pendingStageKey = null
const audioByStage = {}

const readStoredMuted = () => {
  try {
    return window.localStorage.getItem(MUTE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

const writeStoredMuted = (value) => {
  try {
    window.localStorage.setItem(MUTE_STORAGE_KEY, value ? '1' : '0')
  } catch {
    // Ignore storage failures in private mode.
  }
}

const resolveStageKey = (stageKey) =>
  CAT_RUNNING_STAGE_KEYS.includes(stageKey) ? stageKey : CAT_RUNNING_STAGE_KEYS[0]

const getStageAudio = (stageKey) => {
  const resolvedKey = resolveStageKey(stageKey)
  if (!audioByStage[resolvedKey]) {
    const audio = new Audio(STAGE_BGM_SRC[resolvedKey])
    audio.loop = true
    audio.preload = 'auto'
    audioByStage[resolvedKey] = audio
  }
  return audioByStage[resolvedKey]
}

const applyVolume = (audio, volume) => {
  if (!audio) return
  audio.volume = muted ? 0 : volume
}

const pauseAllTracks = () => {
  Object.values(audioByStage).forEach((audio) => {
    audio.pause()
  })
}

const stopAllTracks = () => {
  Object.values(audioByStage).forEach((audio) => {
    audio.pause()
    audio.currentTime = 0
    audio.volume = 0
  })
}

const playAudio = async (audio) => {
  try {
    await audio.play()
    return true
  } catch {
    return false
  }
}

export const catRunningBgm = {
  preload() {
    CAT_RUNNING_STAGE_KEYS.forEach((stageKey) => {
      getStageAudio(stageKey)
    })
  },

  isMuted() {
    return muted
  },

  hasPendingPlayback() {
    return Boolean(pendingStageKey)
  },

  loadMutedPreference() {
    muted = readStoredMuted()
    return muted
  },

  setMuted(value) {
    muted = Boolean(value)
    writeStoredMuted(muted)

    if (!muted && pendingStageKey) {
      return this.playStage(pendingStageKey)
    }

    const audio = activeStageKey ? audioByStage[activeStageKey] : null
    if (audio && !audio.paused) {
      applyVolume(audio, TARGET_VOLUME)
    }

    return Promise.resolve(Boolean(audio && !audio.paused))
  },

  toggleMuted() {
    this.setMuted(!muted)
    return muted
  },

  unlockFromUserGesture() {
    if (muted) return Promise.resolve(false)

    const stageKey = pendingStageKey ?? activeStageKey
    if (!stageKey) return Promise.resolve(false)

    return this.playStage(stageKey)
  },

  async playStage(stageKey) {
    const resolvedKey = resolveStageKey(stageKey)
    const nextAudio = getStageAudio(resolvedKey)

    if (muted) {
      pendingStageKey = resolvedKey
      return false
    }

    pendingStageKey = resolvedKey

    if (activeStageKey === resolvedKey && !nextAudio.paused) {
      pendingStageKey = null
      applyVolume(nextAudio, TARGET_VOLUME)
      return true
    }

    pauseAllTracks()

    nextAudio.currentTime = 0
    applyVolume(nextAudio, TARGET_VOLUME)

    const didPlay = await playAudio(nextAudio)
    if (!didPlay) {
      nextAudio.pause()
      return false
    }

    activeStageKey = resolvedKey
    pendingStageKey = null
    return true
  },

  pause() {
    pauseAllTracks()
  },

  stop() {
    activeStageKey = null
    pendingStageKey = null
    stopAllTracks()
  },
}

export default catRunningBgm
